<template>
  <el-dialog
    v-if="dialogVisible" v-model="dialogVisible" append-to-body :width="width" :modal="true"
    :show-close="false" :class="{ [style.modal]: true, [style.small]: size == 'small' }" :close-on-click-modal="false"
    :destroy-on-close="true"
    @closed="close"
  >
    <div v-if="showClose" :class="style.close" @click="close">
      <SvgIcon icon="Icon-Default-Close" class="w24 h24 text-text1" />
    </div>

    <div v-if="slots.icon || icon" :class="[style.icon, 'text-align-center pb16']">
      <slot v-if="slots.icon" name="icon" />
      <SvgIcon v-else :icon="icon" class="w64 h64" />
    </div>

    <div v-if="slots.header || header" :class="style.header">
      <slot v-if="slots.header" name="header" />
      <div
        v-else
        :class="[style.title, 'fz16 lh24 weight-700 text-text1 mb4 break-word', slots.icon || icon ? 'text-align-center' : (description ? 'pr24 mb4' : 'pr24 mb18')]"
      >
        {{ header }}
      </div>
    </div>
    <div v-if="slots.description || description" :class="style.description">
      <slot v-if="slots.description" name="description" />
      <div
        v-else
        :class="[style.description, 'text-text2 f14 weight500', slots.icon || icon ? 'text-align-center' : 'pr24 mb18']"
      >
        {{ description }}
      </div>
    </div>
    <div v-if="loading" class="flex-row-center items-center">
      <Loading />
    </div>

    <template v-else>
      <div :class="[style.content]">
        <div v-if="slots.default">
          <slot />
        </div>
        <div
          v-else-if="content"
          :class="['fz14 lh22', 'break-word', style.contentText, slots.icon || icon ? 'weight-500 text-align-center' : 'weight-500']"
        >
          {{ content }}
        </div>
      </div>
      <div v-if="slots.footer || footer" :class="[style.footer, 'mt20']">
        <slot v-if="slots.footer" name="footer" />
        <div v-else-if="cancelText || okText" :class="[style.button, 'flex-row-flex-end']">
          <Button v-if="cancelText" plain type="gray" :size="size" v-bind="cancelProps" @click="cancel">
            {{ cancelText }}
          </Button>
          <Button v-if="okText" type="black" :size="size" v-bind="okProps" @click="ok">
            {{ okText }}
          </Button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import Loading from '@c/common/Loading.vue';
const { t } = useI18n();
const slots = useSlots();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  width: {
    type: [Number, String],
    default: 360
  },
  showClose: {
    type: Boolean,
    default: true
  },
  icon: {
    type: String,
    default: ''
  },
  header: {
    type: [String, Boolean, Object],
    default: null
  },
  description: {
    type: [String, Boolean, Object],
    default: null
  },
  content: {
    type: String,
    default: ''
  },
  footer: {
    type: [String, Boolean, Object],
    default: true
  },
  okText: {
    type: [String, Object],
    default: '确定'
  },
  cancelText: {
    type: [String, Object],
    default: '取消'
  },
  size: {
    validator(value: string) {
      return ['default', 'small'].indexOf(value) > -1;
    },
    default: 'default'
  },
  okProps: {
    type: Object,
    default() {
      return {};
    }
  },
  cancelProps: {
    type: Object,
    default() {
      return {};
    }
  },
  okClose: {
    type: Boolean,
    default: true
  },
  cancelClose: {
    type: Boolean,
    default: true
  }
});

const okText = computed(() => {
  if (props.okText) {
    if (props.okText === '确定') {
      return t(props.okText);
    }
    return props.okText;
  }
  return null;
});

const cancelText = computed(() => {
  if (props.cancelText) {
    if (props.cancelText === '取消') {
      return t(props.cancelText);
    }
    return props.cancelText;
  }
  return null;
});

const emit = defineEmits(['update:visible', 'onOk', 'onCancel', 'onClose']);
const dialogVisible = computed({
  set(value: boolean) {
    emit('update:visible', value);
  },
  get() {
    return props.visible;
  }
});

const icon = computed(() => {
  const map: any = {
    success: '64-Filled-Popup_Success',
    warning: '64-Filled-Popup_Alert',
    error: '64-Filled-Popup_Cross'
  };
  if (props.icon) {
    if (map[props.icon]) {
      return map[props.icon];
    }
    return props.icon;
  } else {
    return null;
  }
});

const close = (type = 'close') => {
  dialogVisible.value = false;
  emit('onClose', type);
};

const cancel = () => {
  emit('onCancel');
  if (props.cancelClose) {
    close('cancel');
  }
};

const ok = () => {
  emit('onOk');
  if (props.okClose) {
    close('ok');
  }
};

</script>

<style lang="scss" module="style">
html[dir="rtl"] {
  .close {
    position: absolute;
    right: unset;
    top: 24px;
    z-index: 2;
    left: 24px;
    cursor: pointer;
  }

  .title {
    padding-right: 0;
    padding-left: 32px;
  }
}

.modal {

  &:global(.el-dialog) {
    --el-dialog-bg-color :var(--base-base1);
    max-width: 90vw;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.10), 0 0 0 1px var(--border-border1);
  }
}

.button {
  :global {
    .el-button {
      flex: 1;
    }
  }
}

.title {
  padding-right: 32;
  max-height: 44px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}

.content {
  word-break: break-word;
}

.contentText {
  max-height: 480px;
  overflow-y: auto;
  margin-right: -8px;
  padding-right: 8px;
}

.close {
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 2;
  cursor: pointer;
}

.small {
  &:global(.el-dialog) {
    :global(.el-dialog__body) {
      padding: 16px;
    }
  }

  .button {
    :global {
      .el-button {
        flex: inherit;
      }
    }
  }

  .close {
    right: 16px;
    top: 16px;
  }
}
</style>
